import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, FileText, Upload, ArrowLeft, AlertTriangle, CheckCircle2, Clock, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  type: 'lifting_machine',
  description: '',
  serial_no: '',
  last_inspection_date: '',
  certificate_file: null,
  certificate_filename: '',
  status: 'active',
  notes: '',
};

const typeLabel = (t) => (t === 'lifting_machine' ? 'Lifting Machine' : 'Lifting Gear');

const daysFromToday = (iso) => {
  if (!iso) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(iso);
  target.setHours(0, 0, 0, 0);
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
};

const StatusBadge = ({ iso }) => {
  const d = daysFromToday(iso);
  if (d === null) return <Badge variant="outline">No date</Badge>;
  if (d < 0) return <Badge className="bg-red-600 text-white">Overdue {Math.abs(d)}d</Badge>;
  if (d === 0) return <Badge className="bg-red-500 text-white">Due TODAY</Badge>;
  if (d <= 7) return <Badge className="bg-orange-500 text-white">Due in {d}d</Badge>;
  if (d <= 30) return <Badge className="bg-yellow-500 text-white">Due in {d}d</Badge>;
  return <Badge className="bg-green-600 text-white">OK ({d}d)</Badge>;
};

export const EquipmentManagement = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [filterType, setFilterType] = useState('all');

  const fetch = async () => {
    setLoading(true);
    try {
      const url = filterType === 'all' ? `${BACKEND_URL}/api/equipment` : `${BACKEND_URL}/api/equipment?type=${filterType}`;
      const res = await axios.get(url);
      setItems(res.data || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load equipment'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY });
    setShowModal(true);
  };

  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({
      type: it.type,
      description: it.description || '',
      serial_no: it.serial_no || '',
      last_inspection_date: it.last_inspection_date || '',
      certificate_file: it.certificate_file || null,
      certificate_filename: it.certificate_filename || '',
      status: it.status || 'active',
      notes: it.notes || '',
    });
    setShowModal(true);
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large (max 5MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setForm((f) => ({ ...f, certificate_file: ev.target.result, certificate_filename: file.name }));
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.description || !form.serial_no) {
      toast.error('Description and Serial No. are required');
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/equipment/${editingId}`, form);
        toast.success('Equipment updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/equipment`, form);
        toast.success('Equipment added');
      }
      setShowModal(false);
      fetch();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Save failed'));
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this equipment record?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/equipment/${id}`);
      toast.success('Deleted');
      fetch();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Delete failed'));
    }
  };

  const openCertificate = (dataUrl, filename) => {
    if (!dataUrl) return;
    try {
      // Chrome/Edge block direct navigation to data: URLs since 2021.
      // Convert to a blob URL first so it opens normally in a new tab.
      const [meta, b64] = dataUrl.split(',');
      const mime = (meta.match(/data:(.*?);base64/) || [])[1] || 'application/octet-stream';
      const byteString = atob(b64);
      const len = byteString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) bytes[i] = byteString.charCodeAt(i);
      const blob = new Blob([bytes], { type: mime });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (!win) {
        // popup blocked → force download
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || 'certificate';
        a.click();
      }
      // revoke later to allow tab to load
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Unable to open certificate');
    }
  };

  const downloadCertificate = (eq) => {
    const dataUrl = eq.certificate_file;
    if (!dataUrl) {
      toast.error('No certificate uploaded for this equipment');
      return;
    }
    try {
      const [meta, b64] = dataUrl.split(',');
      const mime = (meta.match(/data:(.*?);base64/) || [])[1] || 'application/octet-stream';
      const byteString = atob(b64);
      const bytes = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
      const blob = new Blob([bytes], { type: mime });
      const url = URL.createObjectURL(blob);
      const safe = (s) => (s || '').replace(/[^A-Za-z0-9._-]+/g, '_');
      const ext = (eq.certificate_filename || '').includes('.')
        ? '.' + eq.certificate_filename.split('.').pop().toLowerCase()
        : '.pdf';
      const fname = `${typeLabel(eq.type).replace(/\s+/g, '_')}_${safe(eq.serial_no)}_${safe(eq.description).slice(0, 30)}${ext}`;
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success(`Downloaded ${fname}`);
    } catch (e) {
      toast.error('Failed to download certificate');
    }
  };

  const sendWhatsAppReminder = (it) => {
    const adminPhone = '6598366186';
    const msg = `🔔 NJK Reminder\n${typeLabel(it.type)}: ${it.description}\nSerial: ${it.serial_no}\nNext Inspection: ${it.next_inspection_date} (${daysFromToday(it.next_inspection_date)}d)`;
    const url = `https://wa.me/${adminPhone}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-40 pb-12">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/admin/billing')} data-testid="back-btn">
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Equipment Documents</h1>
              <p className="text-gray-600">Lifting Machines & Lifting Gears — auto-reminder every 6 months</p>
            </div>
          </div>
          <Button onClick={openCreate} data-testid="add-equipment-btn">
            <Plus size={18} className="mr-2" /> Add Equipment
          </Button>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Equipment List</CardTitle>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-48" data-testid="filter-type">
                <SelectValue placeholder="Filter type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="lifting_machine">Lifting Machines</SelectItem>
                <SelectItem value="lifting_gear">Lifting Gears</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-gray-500">Loading...</p>
            ) : items.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No equipment yet. Click &quot;Add Equipment&quot; to create your first record.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3">Type</th>
                      <th className="text-left p-3">Description</th>
                      <th className="text-left p-3">Serial / Cert No.</th>
                      <th className="text-left p-3">Last Inspection</th>
                      <th className="text-left p-3">Next Inspection</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-left p-3">Cert</th>
                      <th className="text-right p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.id} className="border-b hover:bg-gray-50" data-testid={`equipment-row-${it.id}`}>
                        <td className="p-3">
                          <Badge variant="outline">{typeLabel(it.type)}</Badge>
                        </td>
                        <td className="p-3">{it.description}</td>
                        <td className="p-3 font-mono text-xs">{it.serial_no}</td>
                        <td className="p-3">{it.last_inspection_date || '-'}</td>
                        <td className="p-3">{it.next_inspection_date || '-'}</td>
                        <td className="p-3"><StatusBadge iso={it.next_inspection_date} /></td>
                        <td className="p-3">
                          {it.certificate_file ? (
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => openCertificate(it.certificate_file, it.certificate_filename)}
                                className="text-blue-600 hover:text-blue-800"
                                title="View certificate"
                                data-testid={`cert-view-${it.id}`}
                              >
                                <FileText size={18} />
                              </button>
                              <button
                                type="button"
                                onClick={() => downloadCertificate(it)}
                                className="text-emerald-600 hover:text-emerald-800"
                                title="Download certificate (PDF)"
                                data-testid={`cert-download-${it.id}`}
                              >
                                <Download size={18} />
                              </button>
                            </div>
                          ) : <span className="text-gray-400">-</span>}
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="outline" onClick={() => sendWhatsAppReminder(it)} title="Send WhatsApp reminder" data-testid={`wa-btn-${it.id}`}>
                              📱
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openEdit(it)} data-testid={`edit-btn-${it.id}`}>
                              <Edit size={14} />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => remove(it.id)} data-testid={`delete-btn-${it.id}`}>
                              <Trash2 size={14} className="text-red-600" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="equipment-modal">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Equipment' : 'Add Equipment'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type *</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="form-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lifting_machine">Lifting Machine</SelectItem>
                      <SelectItem value="lifting_gear">Lifting Gear</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="retired">Retired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Description *</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                       placeholder="e.g. 50-ton lorry crane" data-testid="form-description" />
              </div>
              <div>
                <Label>Serial / Cert No. *</Label>
                <Input value={form.serial_no} onChange={(e) => setForm({ ...form, serial_no: e.target.value })}
                       placeholder="e.g. LM:123456x" data-testid="form-serial" />
              </div>
              <div>
                <Label>Last Inspection Date</Label>
                <Input type="date" value={form.last_inspection_date}
                       onChange={(e) => setForm({ ...form, last_inspection_date: e.target.value })}
                       data-testid="form-last-inspection" />
                <p className="text-xs text-gray-500 mt-1">Next inspection will be auto-calculated to +6 months from this date.</p>
              </div>
              <div>
                <Label>Inspection Certificate (PDF/Image, max 5MB)</Label>
                <Input type="file" accept="application/pdf,image/*" onChange={handleFile} data-testid="form-cert" />
                {form.certificate_filename && (
                  <p className="text-xs text-green-600 mt-1">✓ {form.certificate_filename}</p>
                )}
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                          rows={2} data-testid="form-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button onClick={save} data-testid="form-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default EquipmentManagement;
